import { Typography, Button, Menu, MenuItem, Checkbox, FormControlLabel, Tooltip } from "@material-ui/core";
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { useState, useCallback, useMemo, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
    filterButtons: {
        width: "100%",
        minWidth: "120px",
        height: "43px",
        color: "rgba(0, 0, 0, .8)!important",
        paddingRight: "30px",
        marginBottom: "10px",
        textAlign: "left",
        border: "1px solid rgb(196, 196, 196)!important",
        borderRadius: "3px",
        borderBottom: "1px solid #757575!important",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        justifyContent: "space-between",
        paddingLeft: "10px",
        "&:disabled": {
            color: "rgba(0, 0, 0, .3)!important",
        }
    },
    filterButtonsIcon: {
        color: "rgba(0,0,0,.55)!important"
    },
    label: {
        width: '100%',
        position: 'relative',
        '& span:nth-of-type(2)': {
            width: '100%',
        },
    },
}));

const MultiSelect = ({ title, displayText, options, disabled, onSelected, selecteds, ...props }) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState(selecteds || []);

    const isAllSelected = useMemo(() => {
        return selectedOptions.length === options.filter(opt => !opt?.disabled).length;
    }, [selectedOptions, options]);

    useEffect(() => {
        if (Array.isArray(selecteds)) {
            setSelectedOptions(selecteds);
        }
    }, [selecteds]);

    const handleMenuClose = useCallback(() => {
        setAnchorEl(null);
        onSelected(selectedOptions);
    }, [selectedOptions, onSelected]);

    const handleMenuClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleOptionToggle = useCallback((optionKey) => {
        setSelectedOptions(prevSelected => 
            prevSelected.includes(optionKey) ? prevSelected.filter(opt => opt !== optionKey) : [...prevSelected, optionKey]
        );
    }, []);

    const handleSelectAllToggle = useCallback(() => {
        setSelectedOptions(isAllSelected ? [] : options.filter(opt => !opt?.disabled).map(opt => opt.key));
    }, [isAllSelected, options]);

    const renderDisplayText = useMemo(() => {
        if (isAllSelected) return i18n.t("allSelected");
        if (!selectedOptions.length) return displayText;

        const selectedNames = options.filter(opt => selectedOptions.includes(opt.key)).map(opt => opt.value);
        return selectedNames.length > 0 ? selectedNames.join(', ') : displayText;
    }, [isAllSelected, selectedOptions, options, displayText]);

    return (
        <>
            {title && <Typography style={{ marginBottom: "5px" }}>{title}</Typography>}

            <Button
                variant="outlined"
                onClick={handleMenuClick}
                disabled={disabled}
                className={classes.filterButtons}
                {...props}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: 'calc(100% - 30px)' }}>
                    {renderDisplayText}
                </span>
                <ArrowDropDownIcon className={classes.filterButtonsIcon} />
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorReference="anchorPosition"
                anchorPosition={anchorEl ? { top: anchorEl.getBoundingClientRect().bottom + window.scrollY, left: anchorEl.getBoundingClientRect().left + window.scrollX } : undefined}
                PaperProps={{
                    style: {
                        maxHeight: "300px",
                        width: anchorEl?.offsetWidth || "300px",
                    },
                }}
            >
                <MenuItem dense key="select-all">
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                color="primary"
                                checked={isAllSelected}
                                onChange={handleSelectAllToggle}
                            />
                        }
                        label={i18n.t("selectAll")}
                    />
                </MenuItem>
                {Array.isArray(options) && options.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .map(opt => {
                        return (
                            <MenuItem 
                            dense 
                            key={opt.key}
                            disabled={opt?.disabled}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        color="primary"
                                        checked={selectedOptions.includes(opt.key)}
                                        onChange={() => handleOptionToggle(opt.key)}
                                        disabled={opt?.disabled}
                                    />
                                }
                                className={classes.label}
                                label={
                                    <Tooltip title={opt.value}>
                                        <Typography component="label"
                                            style={{
                                                display: 'inline-block',
                                                width: 'calc(100% - 30px)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                paddingTop: "12px"
                                            }}
                                        >
                                            {opt?.customValue ? opt.customValue : opt.value}
                                        </Typography>
                                    </Tooltip>
                                  }
                                disabled={opt.disabled}
                            />
                        </MenuItem>
                    )})}
            </Menu>
        </>
    );
};

export default MultiSelect;